/**
 * English body copy of the thematic articles. Keys match ARTICLES in seoConfig.mjs.
 * `guideAnchor` points to the matching axis section of the pillar guide (EN ids).
 */
export const ARTICLES_EN = {
  skin: {
    axisLabel: 'Axis 1 \u00b7 Facial aesthetics',
    guideAnchor: 'axis-1',
    title: 'Men\u2019s skincare: the minimalist routine that sticks',
    excerpt: 'Three steps, two minutes, every day. The most effective routine is the one you will still follow in six months.',
    imageAlt: 'Minimalist men\u2019s skincare set: cleanser, moisturizer and sunscreen on dark slate',
    related: ['sleep', 'beard', 'routine'],
    sections: [
      { id: 'why', title: 'Why skin changes everything', blocks: [
        { type: 'p', text: 'Skin is the first thing the eye reads on a face: an even, hydrated complexion signals health and care before the haircut or the beard. It is also the axis with the fastest progress: two to four weeks are enough to see a difference.' },
        { type: 'p', text: 'The classic mistake is to multiply products. A complex routine is abandoned after ten days; a three-step routine becomes a reflex.' },
      ] },
      { id: 'three-steps', title: 'The three steps that matter', blocks: [
        { type: 'h3', text: '1. Cleanse (morning and evening)' },
        { type: 'p', text: 'A gentle cleanser, without harsh soap, removes sebum, pollution and sweat without stripping the skin barrier. Lukewarm water, thirty seconds, pat dry.' },
        { type: 'h3', text: '2. Moisturize (morning and evening)' },
        { type: 'p', text: 'Every skin type needs hydration, including oily skin: dehydrated skin produces more sebum. Choose a gel texture for combination to oily skin, a richer cream for dry skin.' },
        { type: 'h3', text: '3. Protect (in the morning)' },
        { type: 'p', text: 'Daily sunscreen (SPF 30 minimum) is the best-documented anti-aging step. It prevents spots, loss of firmness and dullness. A fluid, invisible formula wears effortlessly under a beard.' },
        { type: 'callout', title: 'The 14-day rule', text: 'Never judge a product before two weeks of regular use. Introduce one new product at a time to identify what works.' },
      ] },
      { id: 'skin-type', title: 'Adapting to your skin type', blocks: [
        { type: 'ul', items: [
          'Oily skin: gentle foaming cleanser, non-comedogenic gel moisturizer, blotting paper during the day if needed.',
          'Dry skin: cream cleanser, rich moisturizer with ceramides, avoid very hot water.',
          'Combination skin: gentle cleanser, light moisturizer on the T-zone and richer on the cheeks.',
          'Sensitive skin: fragrance-free formulas, one product at a time, patch test on the inner arm.',
        ] },
      ] },
      { id: 'mistakes', title: 'Common mistakes', blocks: [
        { type: 'ul', items: [
          'Using shower gel or bar soap on the face.',
          'Exfoliating every day: twice a week is enough.',
          'Skipping moisturizer because the skin is oily.',
          'Switching products every week without giving them time to work.',
          'Forgetting the neck and the beard line, which age the same way.',
        ] },
      ] },
      { id: 'plan', title: '7-day plan', blocks: [
        { type: 'ul', items: [
          'Days 1-2: buy three products suited to your skin type, nothing more.',
          'Days 3-7: morning routine (cleanse, moisturize, protect) and evening (cleanse, moisturize); a photo on day 7 in the same light as day 1.',
          'Week 2 onwards: add a gentle weekly exfoliation, then evaluate at day 14 and day 30.',
        ] },
      ] },
    ],
  },

  beard: {
    axisLabel: 'Axis 4 \u00b7 Grooming',
    guideAnchor: 'axis-4',
    title: 'Beard trimming: lines, length and maintenance',
    excerpt: 'A sharp beard comes down to two lines and a length suited to your face shape. The rest is maintenance.',
    imageAlt: 'Barber kit: trimmer, brush, scissors and beard oil on dark wood',
    related: ['hair', 'eyebrows', 'routine'],
    sections: [
      { id: 'face-shape', title: 'Choosing the shape for your face', blocks: [
        { type: 'p', text: 'The beard is a proportion tool. It can lengthen a round face, soften a very square jaw or structure a long face. Start from what you want to correct, not from a photo seen online.' },
        { type: 'ul', items: [
          'Round face: more length at the chin, short sides to lengthen.',
          'Square face: slightly rounded angles, even medium length.',
          'Long face: slightly fuller sides, short chin to balance.',
          'Oval face: almost everything works; focus on sharp lines.',
        ] },
      ] },
      { id: 'two-lines', title: 'The two lines that make it sharp', blocks: [
        { type: 'h3', text: 'The neckline' },
        { type: 'p', text: 'Place two fingers above your Adam\u2019s apple: the line runs there, in a gentle curve from ear to ear. Too high, it narrows the jaw; too low, it looks neglected.' },
        { type: 'h3', text: 'The cheek line' },
        { type: 'p', text: 'Follow the natural limit, removing only stray hairs above it. A cheek line that is too low or too straight looks artificial. When in doubt, leave it.' },
        { type: 'callout', title: 'Mistake #1', text: 'Trimming the neckline at the jaw level. It is the most common mistake and the one that ages the face the most.' },
      ] },
      { id: 'tools', title: 'Tools and length', blocks: [
        { type: 'ul', items: [
          'Trimmer with guards: work from longest to shortest, against the grain then with it.',
          'Scissors: for stray hairs on the mustache and chin.',
          'Boar-bristle brush: tames and distributes oil.',
          'Oil or balm: a few drops on a slightly damp beard, massage down to the skin.',
        ] },
        { type: 'p', text: 'The ideal length is the one that stays sharp between two trims: start one guard longer than you imagine, you can always go shorter.' },
      ] },
      { id: 'maintenance', title: 'Weekly maintenance', blocks: [
        { type: 'ul', items: [
          'Wash 2 to 3 times a week with a beard shampoo, not shower gel.',
          'Touch up both lines every 3 to 4 days with a razor or a guardless trimmer.',
          'Length trim every 1 to 2 weeks.',
          'Barber appointment every 4 to 6 weeks to reset the shape.',
        ] },
      ] },
      { id: 'plan', title: '7-day plan', blocks: [
        { type: 'ul', items: [
          'Day 1: define the neckline, leave the cheek line natural.',
          'Days 2-6: wash, oil, daily brushing; do not re-trim.',
          'Day 7: first length trim with a guard, reference photo.',
        ] },
      ] },
    ],
  },

  posture: {
    axisLabel: 'Axis 2 \u00b7 Body structure',
    guideAnchor: 'axis-2',
    title: 'Improve your posture in 10 minutes a day',
    excerpt: 'Posture is read from ten meters away. Three corrections and a short routine transform your bearing within weeks.',
    imageAlt: 'Silhouette of a man from behind with open shoulders, upright posture in warm light',
    related: ['training', 'sleep', 'presence'],
    sections: [
      { id: 'reading', title: 'What your posture says about you', blocks: [
        { type: 'p', text: 'Before the face, people perceive a silhouette: open or rolled shoulders, an aligned or forward head. A collapsed posture visually reduces your frame, compresses the waist and gives an impression of fatigue, whatever the rest.' },
        { type: 'p', text: 'The good news: it is the fastest lever in the method. The first changes are visible after two to three weeks of daily practice.' },
      ] },
      { id: 'three-fixes', title: 'The three corrections', blocks: [
        { type: 'ul', items: [
          'Shoulders: low and slightly back, without forcing or arching.',
          'Neck: chin tucked one centimeter, crown of the head pulled up, as if hanging from a thread.',
          'Pelvis: neutral, neither tilted forward (arched back) nor backward (rounded back).',
        ] },
        { type: 'callout', title: 'Wall check', text: 'Back to the wall: heels, glutes, shoulder blades and back of the head in contact, one hand just slides into the lower back curve. Memorize this feeling, then step forward keeping it.' },
      ] },
      { id: 'routine', title: 'The 10-minute routine', blocks: [
        { type: 'ul', items: [
          '2 min \u2013 Thoracic opening: hands behind the neck, elbows open, gentle upper-back extension.',
          '2 min \u2013 Chest stretch in a doorway, 30 seconds per side, twice.',
          '3 min \u2013 Upper-back strengthening: band rows or floor \u201cY-T-W\u201d, 3 sets of 10.',
          '2 min \u2013 Hip mobility: low lunges, 30 seconds per side, twice.',
          '1 min \u2013 Chin tucks: 15 slow repetitions.',
        ] },
      ] },
      { id: 'desk', title: 'Desk and phone traps', blocks: [
        { type: 'ul', items: [
          'Screen too low: top of the screen at eye level, one arm\u2019s length away.',
          'Phone at belly height: raise the device, not the head going down.',
          'Fixed position: stand up for two minutes every 45 minutes.',
          'Bag always on the same side: alternate or switch to a backpack.',
        ] },
      ] },
      { id: 'plan', title: '7-day plan', blocks: [
        { type: 'ul', items: [
          'Day 1: side and front photo, standing naturally, without correcting.',
          'Days 1-7: 10-minute routine every morning, wall check in the evening.',
          'Day 7: new photo in the same conditions; compare the ear-shoulder-hip alignment.',
        ] },
      ] },
    ],
  },

  style: {
    axisLabel: 'Axis 3 \u00b7 Clothing style',
    guideAnchor: 'axis-3',
    title: 'Men\u2019s clothing style: fit, colors, coherence',
    excerpt: 'Style is not a matter of budget. Three rules are enough to look dressed with intention.',
    imageAlt: 'Men\u2019s capsule wardrobe: navy overshirt, white tee, grey trousers, leather boots and belt',
    related: ['accessories', 'posture', 'hair'],
    sections: [
      { id: 'fit', title: 'Fit above all', blocks: [
        { type: 'p', text: 'A well-fitted 20-euro garment always beats an oversized branded piece. Fit is the most visible signal of care and the cheapest to correct.' },
        { type: 'ul', items: [
          'Shoulders: the seam sits exactly on the shoulder bone.',
          'Sleeves: at the wrist for a shirt, one centimeter of shirt visible under a jacket.',
          'Torso: you can slide a flat hand, not a fist.',
          'Trousers: a slight break on the shoe, never accordion folds.',
        ] },
        { type: 'callout', title: 'The best investment', text: 'Having the waist and length of three pieces you already wear adjusted costs less than a new outfit and produces an immediate effect.' },
      ] },
      { id: 'palette', title: 'A palette of three neutrals plus one accent', blocks: [
        { type: 'p', text: 'Pick three neutrals that combine effortlessly (navy, charcoal grey, ecru or beige) and one accent color that flatters your complexion: olive green, burgundy, sky blue or rust. Any new piece must pair with at least three others in your wardrobe.' },
        { type: 'p', text: 'Warm (golden) complexion: olive, camel, rust. Cool (pink) complexion: navy, grey, burgundy, ice blue. When in doubt, navy suits everyone.' },
      ] },
      { id: 'register', title: 'A coherent register', blocks: [
        { type: 'p', text: 'Mixing sports sneakers, a formal shirt and joggers sends a muddled message. Choose a register and keep it: polished casual (raw denim, heavy tee, overshirt, minimalist sneakers), smart casual (chinos, fine knit, unstructured jacket, derbies) or formal.' },
        { type: 'ul', items: [
          'Matte, textured fabrics rather than shiny ones.',
          'Discreet or absent logos.',
          'Clean, maintained shoes: the most noticed detail.',
          'One strong accessory at a time: watch, glasses or belt.',
        ] },
      ] },
      { id: 'essentials', title: 'The essentials without a big budget', blocks: [
        { type: 'ul', items: [
          'Two heavy tees (white, heather grey) that fit well.',
          'Raw denim or fitted chinos in a neutral.',
          'A navy overshirt or light jacket.',
          'A fine crew-neck knit.',
          'A pair of minimalist white sneakers and a pair of leather boots or derbies.',
        ] },
      ] },
      { id: 'plan', title: '7-day plan', blocks: [
        { type: 'ul', items: [
          'Day 1: wardrobe sorting; set aside anything too large or worn out.',
          'Days 2-3: define your three neutrals and your accent; photograph three coherent outfits.',
          'Days 4-7: drop three pieces at a tailor, clean your shoes, complete a single missing essential.',
        ] },
      ] },
    ],
  },

  hair: {
    axisLabel: 'Axis 1 \u00b7 Facial aesthetics',
    guideAnchor: 'axis-1',
    title: 'Haircut and face shape: choosing the right cut',
    excerpt: 'The right haircut rebalances facial proportions. It is chosen with a mirror, not with a celebrity photo.',
    imageAlt: 'Barber station: comb, matte clay tin, spray bottle and shears on dark marble',
    related: ['beard', 'skin', 'routine'],
    sections: [
      { id: 'proportions', title: 'The principle: rebalance', blocks: [
        { type: 'p', text: 'A haircut visually adds or removes height and width. The goal is not to follow a trend but to bring the face closer to a balanced oval.' },
        { type: 'ul', items: [
          'Round face: volume and height on top, short sides (fade), avoid heavy fringes.',
          'Square face: soft textures, a bit of length, avoid very geometric cuts.',
          'Long face: slightly fuller sides, less height, a fringe or sweep is possible.',
          'Oval face: great freedom; focus on sharp outlines.',
          'High forehead or fine hair: shorter, textured cuts that add density.',
        ] },
      ] },
      { id: 'at-the-barber', title: 'Talking to your barber', blocks: [
        { type: 'p', text: 'Bring one or two references, but above all explain what you want to correct (\u201cless width on the sides\u201d, \u201cmore volume in front\u201d). State how much time you spend styling in the morning: a demanding cut that is not maintained looks neglected.' },
        { type: 'callout', title: 'Frequency', text: 'Every 3 to 5 weeks for a short cut, 6 to 8 for a medium length. Write down the date: regularity matters more than the cut itself.' },
      ] },
      { id: 'styling', title: 'Products and 2-minute styling', blocks: [
        { type: 'ul', items: [
          'Fine hair: texturizing powder or volume spray on dry hair.',
          'Thick hair: cream or matte clay, applied on slightly damp hair.',
          'Curly hair: defining cream, air dry or diffuser.',
          'In all cases: a pea-sized amount warmed between the palms, back to front, finger finish.',
        ] },
        { type: 'p', text: 'A hair dryer used for thirty seconds to direct the roots changes the cut more than any product.' },
      ] },
      { id: 'mistakes', title: 'Common mistakes', blocks: [
        { type: 'ul', items: [
          'Copying a cut without considering your face shape or hair type.',
          'Too much product, or a shiny product that flattens.',
          'Letting the outlines (neck, sideburns) grow out for three weeks.',
          'Washing daily with a stripping shampoo: two to three times a week is often enough.',
        ] },
      ] },
      { id: 'plan', title: '7-day plan', blocks: [
        { type: 'ul', items: [
          'Day 1: identify your face shape in the mirror, hair pulled back.',
          'Days 2-5: barber appointment with a precise correction goal.',
          'Days 6-7: choose a single suitable product, practice two-minute styling, reference photo.',
        ] },
      ] },
    ],
  },

  presence: {
    axisLabel: 'Axis 5 \u00b7 Social presence',
    guideAnchor: 'axis-5',
    title: 'Social presence: gaze, voice and body language',
    excerpt: 'Presence is the multiplier of all the other axes. It is trained like a skill, with simple exercises.',
    imageAlt: 'A man\u2019s hands resting calmly on a dark table, a watch catching warm light',
    related: ['photo', 'posture', 'style'],
    sections: [
      { id: 'multiplier', title: 'Why presence multiplies the rest', blocks: [
        { type: 'p', text: 'The same face, the same outfit are perceived very differently depending on gaze, voice and gestures. Presence turns a polished whole into an impression of confidence; its absence makes even a good appearance invisible.' },
        { type: 'p', text: 'Good news: contrary to popular belief, it is not a character trait but a set of micro-behaviors that can be trained.' },
      ] },
      { id: 'gaze', title: 'The gaze', blocks: [
        { type: 'ul', items: [
          'Hold eye contact for two to three seconds, then look away slowly to the side, never down.',
          'Blink naturally; a fixed stare makes people uncomfortable.',
          'When listening, look at the person; when thinking, the gaze may briefly wander.',
          'Avoid scanning the room or checking your phone during a conversation.',
        ] },
      ] },
      { id: 'voice', title: 'The voice', blocks: [
        { type: 'p', text: 'A steady voice comes from breathing. Inhale from the belly, speak slightly slower and a bit lower than your reflex, and end your sentences on a falling rather than rising intonation.' },
        { type: 'callout', title: '3-minute exercise', text: 'Read a paragraph aloud while recording yourself: spot swallowed sentence endings and the \u201cums\u201d. Start again, marking a silent pause instead.' },
      ] },
      { id: 'gestures', title: 'Gestures and energy', blocks: [
        { type: 'ul', items: [
          'Visible, calm hands, open gestures toward the other person.',
          'No nervous self-touching (neck, face, hair).',
          'Occupy your space: feet hip-width apart, weight distributed.',
          'Energy shows on the face: sleep, daylight and physical activity count as much as technique.',
        ] },
      ] },
      { id: 'plan', title: '7-day plan', blocks: [
        { type: 'ul', items: [
          'Days 1-2: three seconds of eye contact with every person you talk to.',
          'Days 3-4: a daily three-minute voice recording, working on sentence endings.',
          'Days 5-7: calm, visible hands in meetings or social events; note one situation per day where you kept all three cues.',
        ] },
      ] },
    ],
  },

  sleep: {
    axisLabel: 'Axis 2 \u00b7 Body structure',
    guideAnchor: 'axis-2',
    title: 'Sleep and energy: the visible impact on your face',
    excerpt: 'No product compensates for a bad night. Sleep is the cheapest skincare and presence booster there is.',
    imageAlt: 'Nightstand at dawn: glass of water, analog alarm clock and sleep mask on wood',
    related: ['skin', 'posture', 'routine'],
    sections: [
      { id: 'visible', title: 'What shows after a bad night', blocks: [
        { type: 'p', text: 'Darker circles, droopy eyelids, dull complexion, drawn features, slumped posture, flatter voice: lack of sleep is immediately readable and affects four of the five axes at once.' },
        { type: 'p', text: 'Conversely, a week of full nights visibly improves complexion and perceived energy, without any purchase.' },
      ] },
      { id: 'levers', title: 'The four levers', blocks: [
        { type: 'ul', items: [
          'Regularity: same bedtime and wake-up time, weekends included, within an hour.',
          'Light: ten minutes of daylight within the hour after waking; screens in night mode and dim lights after 9 pm.',
          'Environment: cool (18-19 \u00b0C), dark and quiet bedroom; phone out of the room.',
          'Substances: no caffeine after 2 pm, limited alcohol in the evening (it fragments sleep and puffs up the face).',
        ] },
        { type: 'callout', title: 'Hydration', text: 'A large glass of water on waking and a visible bottle on the desk: dehydration accentuates dark circles and a grey complexion.' },
      ] },
      { id: 'protocol', title: '7-day protocol', blocks: [
        { type: 'ul', items: [
          'Day 1: photo of your face on waking, natural light, no filter. Set a realistic bedtime.',
          'Days 1-7: morning daylight, caffeine before 2 pm, screens off 45 minutes before bed, cool bedroom.',
          'Day 7: same photo, same light. Compare dark circles, complexion and how open the eyes look.',
        ] },
      ] },
      { id: 'mistakes', title: 'Common mistakes', blocks: [
        { type: 'ul', items: [
          'Catching up on weekends: the shift creates a social jet lag readable on Monday.',
          'Relying on concealer without treating the cause.',
          'Intense exercise very late in the evening.',
          'Heavy, late dinner: digestion and deep sleep do not mix.',
        ] },
      ] },
    ],
  },

  routine: {
    axisLabel: 'Axis 4 \u00b7 Grooming',
    guideAnchor: 'axis-4',
    title: 'Routine and consistency: making it last',
    excerpt: 'Motivation always drops. A well-designed routine works without it.',
    imageAlt: 'Open leather notebook with a weekly checklist, fountain pen and espresso cup on a dark desk',
    related: ['skin', 'beard', 'sleep'],
    sections: [
      { id: 'principle', title: 'Why routines fail', blocks: [
        { type: 'p', text: 'Too long, started all at once, without a trigger or a measure: most appearance routines stop in week three. The problem is not discipline but design.' },
      ] },
      { id: 'four-rules', title: 'The four rules of a routine that lasts', blocks: [
        { type: 'ul', items: [
          'Anchoring: attach each step to an existing habit (after brushing teeth \u2192 moisturizer; after coffee \u2192 10 minutes of posture).',
          'Minimum duration: the \u201chard day\u201d version must fit in two minutes. You never skip, you shrink.',
          'External reminders: a notification or an email at a fixed time replaces memory and willpower.',
          'Measurement: a monthly photo in the same conditions and three numeric indicators (full nights, sessions, waist).',
        ] },
        { type: 'callout', title: 'The two-day rule', text: 'Missing a day is normal; never miss two days in a row. It is the only discipline rule you need.' },
      ] },
      { id: 'typical-week', title: 'A typical week (under 20 minutes a day)', blocks: [
        { type: 'ul', items: [
          'Daily: skincare routine (2 min), posture (10 min), styling (2 min), morning daylight.',
          'Monday / Thursday: beard line touch-up.',
          'Wednesday / Sunday: beard wash, gentle exfoliation.',
          'Sunday: outfits prepared for the week, shoes cleaned, indicators reviewed.',
          'Monthly: haircut, progress photo, adjustment of a single parameter.',
        ] },
      ] },
      { id: 'relapses', title: 'Handling relapses', blocks: [
        { type: 'p', text: 'Travel, illness, a busy period: the routine will break. Plan for it. Resume with the minimum version the very next day, without trying to catch up. Guilt costs more than the missed day.' },
      ] },
      { id: 'plan', title: '7-day plan', blocks: [
        { type: 'ul', items: [
          'Day 1: choose three steps maximum and their anchor; schedule a reminder.',
          'Days 2-6: do the full version when possible, the two-minute version otherwise.',
          'Day 7: honest review; add a single step for the following week.',
        ] },
      ] },
    ],
  },

  smile: {
    axisLabel: 'Axis 1 \u00b7 Facial aesthetics',
    guideAnchor: 'axis-1',
    title: 'Smile and teeth: hygiene, whitening, confidence',
    excerpt: 'The smile is the face\u2019s first social signal. Healthy teeth and a relaxed smile matter more than perfect alignment.',
    imageAlt: 'Bamboo toothbrush, dental floss and a glass of water on a dark marble counter',
    related: ['skin', 'eyebrows', 'photo'],
    sections: [
      { id: 'why', title: 'Why the smile carries so much weight', blocks: [
        { type: 'p', text: 'Other people\u2019s eyes travel from your eyes to your mouth in under a second. An open smile with clean teeth signals health, openness and self-care; a closed mouth or a held-back smile signals discomfort, even when the rest of the face is well kept.' },
        { type: 'p', text: 'The good news: most men do not need orthodontics. Impeccable hygiene, regular professional cleaning and the habit of smiling without holding back produce most of the result.' },
      ] },
      { id: 'hygiene', title: 'Hygiene that shows', blocks: [
        { type: 'h3', text: 'Brushing: twice a day, two minutes' },
        { type: 'p', text: 'Soft or electric brush, fluoride toothpaste, gentle strokes from the gum toward the tooth. Brushing hard whitens nothing: it wears enamel and makes gums recede. Wait thirty minutes after an acidic meal before brushing.' },
        { type: 'h3', text: 'Floss or interdental brushes: every evening' },
        { type: 'p', text: 'This is the most skipped step and the one that changes breath and gum color the most. Interdental brushes are easier than floss for most men.' },
        { type: 'h3', text: 'Tongue and breath' },
        { type: 'p', text: 'A tongue scraper in the morning removes the coating responsible for most bad breath. Drink water regularly: a dry mouth always smells stronger.' },
        { type: 'callout', title: 'Professional cleaning', text: 'A dental cleaning every six to twelve months removes what no brush removes and visibly brightens teeth in a single session. It is the most cost-effective investment of this axis.' },
      ] },
      { id: 'whitening', title: 'Whitening: the reasonable and the harmful', blocks: [
        { type: 'ul', items: [
          'Limit stains at the source: coffee, tea, red wine and tobacco. Rinse with water afterwards, use a straw for cold drinks.',
          'Whitening toothpaste: mild effect on surface stains, no effect on natural shade. Do not expect more.',
          'Dentist-supervised whitening (trays or in-office): effective and safe when enamel is healthy.',
          'Aggressive online kits, pure baking soda, charcoal: sensitivity and enamel wear for an unstable result. Avoid.',
        ] },
        { type: 'p', text: 'Aim for a natural shade in harmony with the whites of your eyes. Overly bright white looks artificial and draws attention to the teeth rather than the smile.' },
      ] },
      { id: 'smile', title: 'Learning to smile without holding back', blocks: [
        { type: 'p', text: 'Many men smile with a closed mouth out of habit, not necessity. A smile that slightly reveals the upper teeth, with the eyes crinkling, reads as sincere; a forced smile only moves the mouth.' },
        { type: 'ul', items: [
          'In front of the mirror: think of something genuinely funny and watch which muscles move. That is your reference smile.',
          'Relax the jaw before smiling: tongue on the palate, teeth slightly apart.',
          'In a photo, smile right before the shutter, not three seconds before: a smile freezes quickly.',
        ] },
      ] },
      { id: 'mistakes', title: 'Common mistakes', blocks: [
        { type: 'ul', items: [
          'Brushing hard and fast instead of gently and long.',
          'Skipping floss because gums bleed: that is precisely the sign it must be done every evening.',
          'Postponing the dentist for years, then wanting to fix everything at once.',
          'Chasing an advertising white instead of a natural, even white.',
        ] },
      ] },
      { id: 'plan', title: '7-day plan', blocks: [
        { type: 'ul', items: [
          'Day 1: book a professional cleaning; buy interdental brushes and a tongue scraper.',
          'Days 1-7: gentle brushing twice for two minutes, interdental brushes every evening, water after coffee.',
          'Days 3-7: two minutes of smiling in the mirror each day to set your reference smile; smiling photo on day 7.',
        ] },
      ] },
    ],
  },

  training: {
    axisLabel: 'Axis 2 \u00b7 Body structure',
    guideAnchor: 'axis-2',
    title: 'Strength training and physique: frame, waist, consistency',
    excerpt: 'A V-shaped physique is built with three sessions a week, protein and sleep. No need to live in the gym.',
    imageAlt: 'Cast-iron dumbbells, a dark towel and a water bottle on a gym floor in warm light',
    related: ['posture', 'sleep', 'style'],
    sections: [
      { id: 'physique', title: 'What the physique changes', blocks: [
        { type: 'p', text: 'From a distance, nobody sees skin or haircut: they see a ratio between shoulders, waist and posture. A developed back and shoulders with a contained waist produce the V shape that clothes are designed to flatter.' },
        { type: 'p', text: 'It is the slowest lever of the method (three to six months for a visible change) but also the most durable, and the one that improves posture, energy and sleep at the same time.' },
      ] },
      { id: 'program', title: 'Three sessions are enough', blocks: [
        { type: 'p', text: 'Three 45-minute strength sessions a week, built around movements that recruit many muscles. Progress from one session to the next (one rep, one kilo): progression builds, not fatigue.' },
        { type: 'ul', items: [
          'Pulls (pull-ups, rows, lat pulldown): the priority for frame and posture. Two exercises per session.',
          'Pushes (bench or incline press, push-ups, overhead press): one exercise per session.',
          'Legs (squat, lunges, Romanian deadlift): one exercise per session; trained legs carry the rest.',
          'Short finisher: lateral raises for shoulder width, planks for the waist.',
        ] },
        { type: 'callout', title: 'The 3 x 8-12 rule', text: 'Three sets of 8 to 12 reps per exercise, the last one hard but clean. When you hit 12 everywhere, increase the load. Log everything in an app or a notebook.' },
      ] },
      { id: 'plate', title: 'The plate does half the work', blocks: [
        { type: 'ul', items: [
          'Protein at every meal: meat, fish, eggs, dairy, legumes. Simple cue: a palm-sized portion, three or four times a day.',
          'Vegetables and fruit at every meal, starches adjusted to the day\u2019s activity.',
          'Water: two liters a day; sugary drinks and alcohol show first on the waist and the face.',
          'To trim the waist: a mild deficit (300 to 400 kcal), never a crash diet that melts muscle.',
        ] },
        { type: 'p', text: 'Sleep is the third pillar: seven to nine hours. Without recovery, sessions tire you out without building anything.' },
      ] },
      { id: 'measure', title: 'Measuring without the scale', blocks: [
        { type: 'p', text: 'Weight alone says nothing: you can lose fat and gain muscle without it moving. Track three indicators per month instead.' },
        { type: 'ul', items: [
          'Waist circumference at the navel, in the morning before eating.',
          'Loads or reps on two reference exercises (one pull, one push).',
          'Front and side photo, same light, same time, same posture.',
        ] },
      ] },
      { id: 'mistakes', title: 'Common mistakes', blocks: [
        { type: 'ul', items: [
          'Training only chest and arms: shoulders roll forward and the physique flattens.',
          'Changing programs every two weeks instead of progressing on the same one.',
          'Training six times a week on six hours of sleep.',
          'Judging results after three weeks: the body changes on a quarterly scale.',
        ] },
      ] },
      { id: 'plan', title: '7-day plan', blocks: [
        { type: 'ul', items: [
          'Day 1: reference photo, waist measurement, choose three fixed slots in the week.',
          'Days 2, 4, 6: three 45-minute sessions (two pulls, one push, one leg exercise, finisher). Log the loads.',
          'Every day: protein at each meal, two liters of water, 8,000 steps, fixed bedtime.',
        ] },
      ] },
    ],
  },

  accessories: {
    axisLabel: 'Axis 3 \u00b7 Clothing style',
    guideAnchor: 'axis-3',
    title: 'Men\u2019s accessories: glasses, watch, shoes',
    excerpt: 'Accessories are the details the eye notices last and remembers first. Few, well chosen, at the right scale.',
    imageAlt: 'Acetate glasses, a steel watch and a leather belt laid on dark fabric',
    related: ['style', 'hair', 'photo'],
    sections: [
      { id: 'principle', title: 'The principle: one statement accessory', blocks: [
        { type: 'p', text: 'Accessories tell the intention behind an outfit. Too many and they blur the message; too few and the outfit looks unfinished. The rule is simple: one noticeable accessory (glasses, watch or shoes), the rest discreet and coherent.' },
        { type: 'p', text: 'Second rule: metals match each other (belt buckle, watch, glasses temples) and so do leathers (belt and shoes in close shades).' },
      ] },
      { id: 'glasses', title: 'Glasses: face shape first', blocks: [
        { type: 'p', text: 'Glasses are the accessory that changes how a face is read the most, since they frame the gaze. The principle is contrast: the frame balances what is most pronounced in the face.' },
        { type: 'ul', items: [
          'Round face: rectangular or square frames with sharp angles, slightly wider than the face.',
          'Square face: round or oval frames, thin, which soften the jaw.',
          'Long face: tall frames (aviator, thick square) that break up the height; avoid narrow frames.',
          'Oval face: almost anything works; favor a frame exactly as wide as your temples.',
        ] },
        { type: 'callout', title: 'Fit cues', text: 'Eyebrows sit just above the top of the frame without disappearing behind it; eyes are centered in the lenses; the frame does not touch the cheeks when you smile. For sunglasses, same rules, lens quality before brand.' },
      ] },
      { id: 'watch', title: 'Watch: the scale of your wrist', blocks: [
        { type: 'ul', items: [
          'Diameter: 38 to 40 mm for a thin or medium wrist, 40 to 42 mm for a wide wrist. The case should not extend past the wrist bone.',
          'Fitted strap: the watch neither slides onto the hand nor marks the skin.',
          'Leather for smart casual and formal, steel or fabric for casual. One versatile watch beats three poorly chosen ones.',
          'A sober dial (two or three colors) crosses registers; a busy dial locks you into one.',
        ] },
      ] },
      { id: 'shoes', title: 'Shoes and belt: the most looked-at detail', blocks: [
        { type: 'p', text: 'An outfit is judged from the bottom up. Clean, polished shoes with sound soles lift a simple outfit; worn shoes drag down an expensive one.' },
        { type: 'ul', items: [
          'Three pairs cover everything: minimalist white sneakers, brown leather boots or derbies, one black or dressier pair depending on your daily life.',
          'Care: brush after each wear, cream or polish every two weeks, cedar shoe trees, a rest day between two wears.',
          'Belt in the shade of your shoe leather, sober buckle in the metal of your watch, 3 to 3.5 cm wide.',
          'Socks: plain, matched to the trousers or the shoes, never white sports socks outside of sport.',
        ] },
      ] },
      { id: 'mistakes', title: 'Common mistakes', blocks: [
        { type: 'ul', items: [
          'Stacking a loud watch, bracelets, a ring, colored glasses and patterned shoes.',
          'A watch too large for the wrist, bought for the brand.',
          'Glasses picked from a celebrity without considering your own face shape.',
          'Neglecting the care of shoes and belt, which show with every step.',
        ] },
      ] },
      { id: 'plan', title: '7-day plan', blocks: [
        { type: 'ul', items: [
          'Day 1: identify your face shape and measure your wrist; sort through your accessories.',
          'Days 2-4: clean and condition all your shoes; match belt and shoes; choose your statement accessory.',
          'Days 5-7: try three frames at an optician using the cues above; front photo with and without glasses to compare.',
        ] },
      ] },
    ],
  },

  eyebrows: {
    axisLabel: 'Axis 4 \u00b7 Grooming',
    guideAnchor: 'axis-4',
    title: 'Eyebrows and finishing touches: grooming details',
    excerpt: 'Eyebrows, nose hair, nails, hands, fragrance: five minutes a week that separate a well-kept face from a neglected one.',
    imageAlt: 'Tweezers, small scissors, a nail file and a fragrance bottle on a dark marble tray',
    related: ['beard', 'skin', 'routine'],
    sections: [
      { id: 'details', title: 'Why details make the sharpness', blocks: [
        { type: 'p', text: 'The brain does not itemize a face, it reads it globally: sharp or neglected. A visible nose hair, a unibrow, bitten nails are enough to tip the reading, even with a perfect haircut.' },
        { type: 'p', text: 'These finishing touches cost a few minutes a week and require no talent. They are the best effort-to-result ratio in all of grooming.' },
      ] },
      { id: 'eyebrows', title: 'Eyebrows: tidy, never thin', blocks: [
        { type: 'p', text: 'Full, well-defined eyebrows structure the gaze. The goal is not to redraw them but to remove what strays from their natural shape.' },
        { type: 'ul', items: [
          'Between the brows: tweeze the stray hairs. The inner limit sits vertically above the inner corner of the eye.',
          'Above and below: only the hairs clearly away from the line. Never touch the thickness.',
          'Length: brush upward and trim with small scissors only what clearly sticks out.',
          'Frequency: ten minutes every two to three weeks, after the shower, in natural light.',
        ] },
        { type: 'callout', title: 'The red line', text: 'Thinned, over-arched or shortened eyebrows are noticed immediately and look artificial. When in doubt, ask a barber for a first clean-up and maintain it afterwards.' },
      ] },
      { id: 'nose-ears', title: 'Nose, ears, neckline', blocks: [
        { type: 'ul', items: [
          'Nose hair: dedicated trimmer once a week, never tweezers (painful, infection risk).',
          'Ears: check the outer ear and lobe in the mirror under raking light; trimmer or small scissors.',
          'Neckline and edges: refresh the edges between two haircuts, or ask the barber every two weeks.',
        ] },
      ] },
      { id: 'hands', title: 'Nails and hands', blocks: [
        { type: 'p', text: 'Hands are looked at when you talk, greet, pay. Short, clean, filed nails, pushed-back cuticles and moisturized skin are noticed more than you think.' },
        { type: 'ul', items: [
          'Cut nails straight, file the corners, once a week after the shower.',
          'Nail brush in the shower; hand cream in the evening, especially in winter.',
          'If you bite your nails: bitter polish and busy hands; progress shows within three weeks.',
        ] },
      ] },
      { id: 'fragrance', title: 'Fragrance: a discreet signature', blocks: [
        { type: 'p', text: 'Fragrance should be noticed at arm\u2019s length, not across the room. Two sprays on clean skin (neck and wrist, or chest) are enough; do not rub, do not spray on clothes.' },
        { type: 'ul', items: [
          'Choose a fragrance on your skin after an hour, not on a paper strip.',
          'One everyday signature (fresh, woody or aromatic) and possibly a second one for the evening.',
          'Deodorant should be neutral or from the same family, never competing.',
        ] },
      ] },
      { id: 'mistakes', title: 'Common mistakes', blocks: [
        { type: 'ul', items: [
          'Thinning or arching the eyebrows: the most visible mistake and the longest to recover from.',
          'Tweezing nose hair.',
          'Too much fragrance, or fragrance applied on unwashed skin.',
          'Long or bitten nails with a sharp outfit: the inconsistency gets noticed.',
        ] },
      ] },
      { id: 'plan', title: '7-day plan', blocks: [
        { type: 'ul', items: [
          'Day 1: buy tweezers, small scissors, a nose-ear trimmer and a file; photo of your gaze in natural light.',
          'Day 2: eyebrow clean-up (between the brows and strays only), nose, ears; nails cut and filed.',
          'Days 3-7: hand cream every evening; test a fragrance on skin for a full day; schedule the fortnightly reminder.',
        ] },
      ] },
    ],
  },

  photo: {
    axisLabel: 'Axis 5 \u00b7 Social presence',
    guideAnchor: 'axis-5',
    title: 'A better photo: light, angle, expression',
    excerpt: 'Your profile photo is seen more often than your face. It comes down to three variables: light, angle and expression.',
    imageAlt: 'Mirrorless camera and a small reflector set near a window in soft natural light',
    related: ['presence', 'smile', 'accessories'],
    sections: [
      { id: 'stakes', title: 'The photo: a first impression at scale', blocks: [
        { type: 'p', text: 'Professional networks, messaging, apps: most people see you in a photo first. A bad photo of a well-kept face wastes all the work of the other axes; a good photo of an ordinary face captures attention.' },
        { type: 'p', text: 'Being photogenic is not a gift. It is well-placed light, an angle that respects your proportions and an expression you have rehearsed. It is also the basis of a reliable analysis: a sharp, well-lit photo gives the coach accurate cues.' },
      ] },
      { id: 'light', title: 'Light is 70% of the result', blocks: [
        { type: 'ul', items: [
          'Natural light, facing a window, no direct sun: it smooths the skin and lights up the eyes.',
          'Avoid ceiling light (shadows under the eyes and nose) and frontal flash (flat complexion, shine).',
          'Slightly to the side (45\u00b0) to sculpt the jaw; straight on for a softer look.',
          'Simple, dark or neutral background one or two meters behind you to separate the face.',
        ] },
        { type: 'callout', title: 'Golden hour', text: 'The hour after sunrise or before sunset gives a warm, low light that flatters every complexion. Indoors, a north-facing window gives stable light all day long.' },
      ] },
      { id: 'angle', title: 'Angle and posture', blocks: [
        { type: 'ul', items: [
          'Lens at eye level or slightly above; never from below (double chin, nostrils).',
          'Distance: at arm\u2019s length minimum, ideally one meter with a self-timer; phones distort faces in close-up.',
          'Chin slightly forward then down by a centimeter: the jaw defines itself, the neck lengthens.',
          'Shoulders open and turned 15-30\u00b0, head brought back toward the lens; strictly frontal photos widen.',
        ] },
      ] },
      { id: 'expression', title: 'Expression: relaxed above all', blocks: [
        { type: 'p', text: 'The most effective expression is a relaxed face with a light smile and engaged eyes. Exhale right before the shutter, think of someone you like, and let the eyes crinkle a little.' },
        { type: 'ul', items: [
          'Look at the lens, not the screen: that is what creates eye contact in the photo.',
          'Take twenty photos in a burst and choose afterwards; the good one is rarely the first.',
          'For a PrimeFace analysis: one neutral front photo and one profile, no glasses or cap, hair off the face, same light every time.',
        ] },
      ] },
      { id: 'editing', title: 'Editing: the honest minimum', blocks: [
        { type: 'p', text: 'Crop (the face fills 60 to 70% of the image, eyes in the upper third), fix exposure and white balance, that is all. Filters that smooth skin or slim the face are spotted and discredit the photo, and they distort any progress analysis.' },
      ] },
      { id: 'mistakes', title: 'Common mistakes', blocks: [
        { type: 'ul', items: [
          'Low-angle selfie under a ceiling light.',
          'Cropped group photo, blurry or five years old.',
          'Forced smile held too long, or eyes drifting to the screen.',
          'Filters and smoothing that make the photo unrecognizable in person.',
        ] },
      ] },
      { id: 'plan', title: '7-day plan', blocks: [
        { type: 'ul', items: [
          'Day 1: find the best window in your home and the hour when its light is soft; clean the lens.',
          'Days 2-3: five minutes of expression practice in the mirror (light smile, chin forward-down, open shoulders).',
          'Days 4-7: a twenty-photo self-timer session at one meter, neutral background; select two photos (public profile + neutral photo for your analysis).',
        ] },
      ] },
    ],
  },
};
